<template>
  <div>
  <div class="px-3 md:px-0" v-if="!loading">
    <OlimpiadasLiveSessionBanner v-if="moduleId === 636" variant="app" class="mb-4" />
    <h1 class="text-lg font-bold text-neutral-900">Fases de la olimpiada</h1>
    <p class="text-neutral-600 mt-2 text-sm">
      Avanza por cada fase completando los exámenes y simulacros de entrenamiento de cada una.
    </p>

    <div
      v-for="phase in sortedPhases"
      :key="phase.id"
      class="mt-5 rounded-xl border p-4 md:p-5"
      :class="phaseCardClass(phase.id)"
    >
      <div class="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 class="text-base font-bold text-neutral-900">{{ phase.name }}</h2>
          <span v-if="phase.isFinal" class="text-xs font-medium text-amber-700">Fase final</span>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <router-link
            v-if="isPhaseAccessible(phase.id) && phaseHasRankingExam(phase)"
            :to="rankingRoute(phase.id)"
            class="text-xs font-semibold text-amber-800 hover:text-amber-900 underline"
          >
            {{ phase.rankingReleased ? "Ver ranking" : "Ranking (pendiente)" }}
          </router-link>
          <span
            class="text-xs font-semibold px-2.5 py-1 rounded-full"
            :class="phaseBadgeClass(phase.id)"
          >
            {{ phaseStatusLabel(phase.id) }}
          </span>
        </div>
      </div>

      <template v-if="isPhaseAccessible(phase.id)">
        <div v-if="phase.exams && phase.exams.length" class="mt-4">
          <h3 class="text-sm font-semibold text-neutral-700 mb-2">Exámenes</h3>
          <ul class="space-y-2">
            <li v-for="ex in phase.exams" :key="ex.id">
              <router-link
                :to="`/course/${moduleId}/exams/${ex.examId}`"
                class="flex items-center justify-between gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2.5 hover:border-amber-400 hover:bg-amber-50/50 transition-colors"
              >
                <span class="font-medium text-neutral-800">{{ ex.examTitle }}</span>
                <span v-if="ex.countsForRanking" class="text-xs text-amber-700 shrink-0">Ranking</span>
              </router-link>
            </li>
          </ul>
        </div>

        <div v-if="phase.simulacrums && phase.simulacrums.length" class="mt-4">
          <h3 class="text-sm font-semibold text-neutral-700 mb-2">Simulacros</h3>
          <ul class="space-y-2">
            <li v-for="sim in phase.simulacrums" :key="sim.id">
              <button
                type="button"
                class="w-full flex items-center justify-between gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2.5 font-medium text-neutral-800 hover:border-amber-400 hover:bg-amber-50/50 transition-colors text-left"
                @click="openSimulacrum(sim)"
              >
                <span>{{ sim.simulacrumTitle }}</span>
                <span
                  v-if="isSimulacrumPremium(sim.simulacrumId)"
                  class="inline-flex items-center px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800 rounded shrink-0"
                >
                  Premium
                </span>
              </button>
            </li>
          </ul>
        </div>

      </template>

      <p v-else class="mt-3 text-sm text-neutral-500">
        Esta fase se desbloqueará cuando completes la fase anterior.
      </p>
    </div>
  </div>
  <div v-else class="px-3 py-8 text-center text-neutral-500">Cargando fases…</div>
  <MobPaymentModal ref="mobPaymentModal" />
  </div>
</template>

<script>
import OlimpiadasLiveSessionBanner from "@/views/V2/olimpiadas/OlimpiadasLiveSessionBanner.vue";
import MobPaymentModal from "@/views/V2/payment-premium/PaymentModal.vue";
import { getOlympicBySlug, getStudentState } from "@/services/olympics.service";
import { getStudentStatusSimulacrum } from "@/services/simulacrum.service";

export default {
  name: "CourseOlympicPhases",
  components: { OlimpiadasLiveSessionBanner, MobPaymentModal },
  data() {
    return {
      loading: true,
      olympicId: null,
      slug: null,
      phases: [],
      unlockedPhaseIds: [],
      passedPhaseIds: [],
      simulacrumPremiumById: {},
    };
  },
  computed: {
    moduleId() {
      return Number(this.$route.params.id);
    },
    sortedPhases() {
      return [...this.phases].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    },
    isPremiumUser() {
      return this.$store.state.auth.isPremium;
    },
  },
  async mounted() {
    if (this.isPremiumUser === null) {
      await this.$store.dispatch("auth/fetchPremiumStatus");
    }
    const slug =
      this.$route.query.slug ||
      this.$store.state.course.olympic?.slug ||
      "olimpiadas-mobius-2026";
    try {
      const simulacrums = await getStudentStatusSimulacrum(this.moduleId);
      this.simulacrumPremiumById = Object.fromEntries(
        (simulacrums || []).map((s) => [s.id, !!s.isPremium])
      );
      const { data: olympic } = await getOlympicBySlug(slug);
      this.olympicId = olympic.id;
      this.slug = olympic.slug || slug;
      this.phases = olympic.phases || [];
      const { data: state } = await getStudentState(olympic.id);
      this.unlockedPhaseIds = state.unlockedPhaseIds || [];
      this.passedPhaseIds = state.passedPhaseIds || [];
    } catch (e) {
      console.error("Error loading olympic phases:", e);
      this.$toasted?.error("No pudimos cargar las fases de la olimpiada.", {
        position: "top-center",
        duration: 3000,
      });
    } finally {
      this.loading = false;
    }
  },
  methods: {
    isPhaseAccessible(phaseId) {
      return (
        this.unlockedPhaseIds.includes(phaseId) || this.passedPhaseIds.includes(phaseId)
      );
    },
    phaseStatusLabel(phaseId) {
      if (this.passedPhaseIds.includes(phaseId)) return "Completada";
      if (this.unlockedPhaseIds.includes(phaseId)) return "En curso";
      return "Bloqueada";
    },
    phaseBadgeClass(phaseId) {
      if (this.passedPhaseIds.includes(phaseId)) return "bg-emerald-100 text-emerald-800";
      if (this.unlockedPhaseIds.includes(phaseId)) return "bg-amber-100 text-amber-900";
      return "bg-neutral-100 text-neutral-600";
    },
    phaseCardClass(phaseId) {
      if (this.passedPhaseIds.includes(phaseId)) return "border-emerald-200 bg-emerald-50/30";
      if (this.unlockedPhaseIds.includes(phaseId)) return "border-amber-300 bg-amber-50/40";
      return "border-neutral-200 bg-neutral-50/50 opacity-90";
    },
    phaseHasRankingExam(phase) {
      return (phase.exams || []).some((ex) => ex.countsForRanking);
    },
    rankingRoute(phaseId) {
      const path = this.slug
        ? `/olimpiadas/${this.slug}/fases/${phaseId}/ranking`
        : `/olimpiadas/id/${this.olympicId}/fases/${phaseId}/ranking`;
      return {
        path,
        query: {
          olympicId: this.olympicId,
          moduleId: this.moduleId,
          ...(this.slug ? { slug: this.slug } : {}),
        },
      };
    },
    isSimulacrumPremium(simulacrumId) {
      return !!this.simulacrumPremiumById[simulacrumId];
    },
    openSimulacrum(sim) {
      if (this.isSimulacrumPremium(sim.simulacrumId) && !this.isPremiumUser) {
        this.$refs.mobPaymentModal.showModalPayment();
        return;
      }
      this.$router.push(`/course/${this.moduleId}/simulacrums/${sim.simulacrumId}`);
    },
  },
};
</script>
